import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { orderService } from "../services/order";
import { formatDateForDisplay } from "../utils/dateUtils";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const Orders = () => {
  const location = useLocation();
  const [emailConfirmation, setEmailConfirmation] = useState(location.state?.emailConfirmation);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    client_name: "",
    order_number: "",
    status: "",
    start_date: "",
    end_date: "",
    is_weekly: "",
  });
  // Campos de texto livre vivem num estado separado e só entram em `filters`
  // (que dispara a busca ao backend) após 4s sem digitar.
  const [textFilters, setTextFilters] = useState({
    client_name: "",
    order_number: "",
  });
  const debouncedTextFilters = useDebouncedValue(textFilters, 4000);
  const isFirstTextFilterRun = useRef(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    start_date: "",
    end_date: "",
    status: [], // Mudado para array para multi-select
    format: "xlsx",
  });
  const [exportLoading, setExportLoading] = useState(false);
  const itemsPerPage = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const data = await orderService.getOrders(filters);
        setOrders(Array.isArray(data) ? data : []);
        setError("");
      } catch (err) {
        // Em caso de erro, definir orders como vazio (não mostrar erro)
        console.error("Erro ao buscar encomendas:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [filters]); // Re-fetch when filters change

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'true' : '') : value,
    }));
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
  };

  // Campos de texto (nome do cliente, nº da encomenda): atualiza o input na
  // hora, mas só propaga para `filters`/backend depois do debounce acima.
  const handleTextFilterChange = (e) => {
    const { name, value } = e.target;
    setTextFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (isFirstTextFilterRun.current) {
      isFirstTextFilterRun.current = false;
      return;
    }
    setFilters((prev) => ({ ...prev, ...debouncedTextFilters }));
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', '1');
      return newParams;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTextFilters]);

  const handleCreateOrder = () => {
    navigate("/create-order");
  };

  // Calculate pagination
  const totalItems = orders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = orders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const handleViewDetails = (id) => {
    navigate(`/order-details/${id}`);
  };

  const handleExportFilterChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'status' && type === 'checkbox') {
      // Handle multi-select for status
      setExportFilters((prev) => {
        const newStatus = checked
          ? [...prev.status, value]
          : prev.status.filter(s => s !== value);
        return {
          ...prev,
          status: newStatus
        };
      });
    } else {
      setExportFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleExport = async () => {
    // Validação: impedir exportação sem data selecionada
    if (!exportFilters.start_date || !exportFilters.end_date) {
      setError("Por favor, selecione as datas de início e fim para exportar.");
      return;
    }

    // Validar se data inicial é menor que data final
    if (new Date(exportFilters.start_date) > new Date(exportFilters.end_date)) {
      setError("A data inicial deve ser anterior à data final.");
      return;
    }

    try {
      setExportLoading(true);
      setError(""); // Limpar erro anterior

      // Converter array de status para formato esperado pelo backend
      const filtersForExport = {
        ...exportFilters,
        status: exportFilters.status.length > 0 ? exportFilters.status.join(',') : '',
        format: exportFilters.format || 'xlsx',
      };

      await orderService.exportOrders(filtersForExport);
    } catch (err) {
      setError(err.message || "Erro ao exportar encomendas");
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const openExportModal = () => {
    setExportFilters({
      start_date: filters.start_date,
      end_date: filters.end_date,
      status: filters.status ? [filters.status] : [], // Converter para array
      format: "xlsx",
    });
    setError(""); // Limpar erros ao abrir modal
    setShowExportModal(true);
  };

  const getOrderEstadoDisplay = (order) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zera a hora para comparação de datas

    // Criar data local para evitar problemas de timezone
    const deliveryDate = new Date(order.delivery_date + 'T00:00:00');
    deliveryDate.setHours(0, 0, 0, 0); // Zera a hora para comparação de datas

    let statusLabel =
      orderEstadoLabels[order.delivery_status] || "Desconhecido";
    let statusColorClass =
      orderEstadoColors[order.delivery_status] ||
      "bg-neutral-100 text-neutral-800";

    if (order.delivery_status === "delivered") {
      statusLabel = "Entregue";
      statusColorClass = "bg-green-100 text-green-800";
    } else if (deliveryDate < today) {
      statusLabel = "Atrasada";
      statusColorClass = "bg-red-100 text-red-800";
    } else if (order.delivery_status === "pending") {
      statusLabel = "Pendente";
      statusColorClass = "bg-yellow-100 text-yellow-800";
    } else if (order.delivery_status === "packed") {
      statusLabel = "Embalado";
      statusColorClass = "bg-purple-100 text-purple-800";
    } else if (order.delivery_status === "overdue") {
      statusLabel = "Atrasado";
      statusColorClass = "bg-orange-100 text-orange-800";
    }
    // Adicione mais condições conforme necessário para outros status específicos

    return { label: statusLabel, colorClass: statusColorClass };
  };

  const orderEstadoColors = {
    pending: "bg-yellow-100 text-yellow-800",
    packed: "bg-purple-100 text-purple-800",
    delivered: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
    overdue: "bg-orange-100 text-orange-800"
  };

  const orderEstadoLabels = {
    pending: "Pendente",
    packed: "Embalado",
    delivered: "Entregue",
    cancelled: "Cancelado",
    overdue: "Atrasado"
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      {emailConfirmation && (
        <div role="status" className={`mb-4 rounded border p-4 ${emailConfirmation.status === 'sent' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-yellow-50 border-yellow-300 text-yellow-800'}`}>
          <div className="flex justify-between items-center gap-4">
            <span>{emailConfirmation.message}</span>
            <button type="button" aria-label="Fechar aviso" onClick={() => {
              setEmailConfirmation(null);
              navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
            }}>×</button>
          </div>
        </div>
      )}
      {/* Header responsivo */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-800 mb-4">Encomendas</h1>
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <button
            onClick={openExportModal}
            className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
          >
            Exportar Encomendas
          </button>
          <button
            onClick={handleCreateOrder}
            className="w-full sm:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800"
          >
            Nova Encomenda
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-4 sm:p-6 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Nome do Cliente
            </label>
            <input
              type="text"
              name="client_name"
              value={textFilters.client_name}
              onChange={handleTextFilterChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Número da Encomenda
            </label>
            <input
              type="text"
              name="order_number"
              value={textFilters.order_number}
              onChange={handleTextFilterChange}
              placeholder="Ex: ENC-001"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Estado
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            >
              <option value="">Todos</option>
              {Object.keys(orderEstadoLabels).map((status) => (
                <option key={status} value={status}>
                  {orderEstadoLabels[status]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Data Inicial
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-neutral-700 mb-1 sm:mb-2">
              Data Final
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-neutral-300 rounded-md text-xs sm:text-sm"
            />
          </div>

          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="is_weekly"
                checked={filters.is_weekly === 'true'}
                onChange={handleFilterChange}
                className="w-4 h-4 rounded border-neutral-300 accent-neutral-900"
              />
              <span className="text-xs sm:text-sm text-neutral-700">Apenas Semanais</span>
            </label>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p>A carregar encomendas...</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-100 border-b sticky left-0">
                <tr>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Número da Encomenda
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Data de Entrega
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Unidades
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {paginatedOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                      {order.order_number}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                      {order.client_name}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                      {formatDateForDisplay(order.delivery_date)}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                      {order.total_units || 0}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <span
                        className={`px-1 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full ${
                          getOrderEstadoDisplay(order).colorClass
                        }`}
                      >
                        {getOrderEstadoDisplay(order).label}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewDetails(order.id)}
                        className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                      >
                        Ver Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginatedOrders.length === 0 && (
            <div className="text-center py-12">
              <div className="text-neutral-500 mb-6">
                <p className="text-lg mb-2">Nenhuma encomenda encontrada</p>
                <p className="text-sm">Crie uma nova encomenda para começar</p>
              </div>
              <button
                onClick={handleCreateOrder}
                className="px-6 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
              >
                Criar Encomenda
              </button>
            </div>
          )}
        </div>
      )}

      {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 px-4 py-3 bg-white border-t">
            <div className="text-sm text-neutral-600">
              Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} encomendas
            </div>
            <nav className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>

              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNumber;
                if (totalPages <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage <= 2) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNumber = totalPages - 2 + index;
                } else {
                  pageNumber = currentPage - 1 + index;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      currentPage === pageNumber
                        ? 'bg-neutral-900 text-white'
                        : 'border border-neutral-200 hover:bg-neutral-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Seguinte
              </button>
            </nav>
          </div>
        )}

      {/* Modal de Exportação */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-neutral-800 mb-4">
              Exportar Encomendas
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={exportFilters.start_date}
                  onChange={handleExportFilterChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={exportFilters.end_date}
                  onChange={handleExportFilterChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Estado (selecione múltiplos)
                </label>
                <div className="border border-neutral-300 rounded-md p-3 max-h-40 overflow-y-auto">
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={exportFilters.status.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setExportFilters(prev => ({ ...prev, status: [] }));
                          }
                        }}
                        className="rounded border-neutral-300"
                      />
                      <span className="text-sm">Todos</span>
                    </label>
                    {Object.keys(orderEstadoLabels).map((status) => (
                      <label key={status} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="status"
                          value={status}
                          checked={exportFilters.status.includes(status)}
                          onChange={handleExportFilterChange}
                          className="rounded border-neutral-300"
                        />
                        <span className="text-sm">{orderEstadoLabels[status]}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {exportFilters.status.length > 0 && (
                  <div className="mt-2 text-xs text-neutral-600">
                    Selecionados: {exportFilters.status.map(s => orderEstadoLabels[s]).join(', ')}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Formato
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setExportFilters(prev => ({ ...prev, format: "xlsx" }))}
                    className={`flex-1 py-2 text-sm rounded-md border font-medium transition-colors ${
                      exportFilters.format === "xlsx"
                        ? "bg-neutral-900 text-white border-neutral-900"
                        : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                    }`}
                  >
                    XLSX
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportFilters(prev => ({ ...prev, format: "pdf" }))}
                    className={`flex-1 py-2 text-sm rounded-md border font-medium transition-colors ${
                      exportFilters.format === "pdf"
                        ? "bg-neutral-900 text-white border-neutral-900"
                        : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
                    }`}
                  >
                    PDF
                  </button>
                </div>
              </div>
            </div>

            {/* Exibir erro de validação no modal */}
            {error && (
              <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
                disabled={exportLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? "Exportando..." : "Exportar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
